import React, { useState, useEffect } from "react";
import {
    Button,
    TextField,
    Skeleton,
    Switch,
    useSnackbar,
    TextArea,
    Select
} from "@livingdesign/react";
import { Plus } from "@livingdesign/icons";
import MuiDataTable from "../../../components/Table/MuiDataTable/MuiDataTable";
import { getService } from "../../../axios/axios";
import { PRACTICE_AREA_SERVICE } from "../../../constants/baseURLs";
import { getErrorMessage } from "../../../helpers/utils";
import { AddBudgetByPhaseStyles } from "./AddBudgetByPhase.styles";
import { amountFormat } from "../../../helpers/commonUtils";
import { NO_PHASES_LINKED_MESSAGE } from "../../../constants/constants";

const AddBudgetByPhase = ({
    loading,
    dataLoading,
    data,
    practiceAreaCode,
    setLoading,
    onPhasesChange,
}) => {
    const [phaseData, setPhaseData] = useState([]);
    const [phaseEntries, setPhaseEntries] = useState([]);
    const classes = AddBudgetByPhaseStyles();
    const { addSnack } = useSnackbar();

    const getPhaseData = () => {
        setLoading(true);
        getService(
            PRACTICE_AREA_SERVICE,
            `/practice-areas/practice-areas-phase-details/v1/search/${practiceAreaCode?.toLowerCase()}`
        )
            .then((response) => {
                setLoading(false);
                const transformedData = response?.data?.map(item => ({
                    id: item.id,
                    code: item.phaseCode,
                    displayName: item.phaseName
                })) || [];
                setPhaseData(transformedData);
            })
            .catch((error) => {
                setLoading(false);
                addSnack({
                    message: getErrorMessage(error)
                })
            });
    };

    useEffect(() => {
        if (practiceAreaCode) {
            getPhaseData();
        }
    }, [practiceAreaCode]);

    useEffect(() => {
        // Initialize with one empty entry when phase data is loaded
        if (phaseData?.length && phaseEntries.length === 0) {
            if (data?.budgetPhases && data.budgetPhases.length > 0) {
                // Edit mode: populate with existing phase data
                const existingPhases = data.budgetPhases.map((phase, index) => ({
                    id: index + 1,
                    code: phase.code || "",
                    displayName: phase.displayName || "",
                    workDescription: phase.workDescription || "",
                    numberOfHours: phase.numberOfHours || 0,
                    budgetAmount: phase.budgetAmount || 0
                }));
                setPhaseEntries(existingPhases);
            } else {
                // Create mode: initialize with one empty entry
                setPhaseEntries([{
                    id: 1,
                    code: "",
                    displayName: "",
                    workDescription: "",
                    numberOfHours: 0,
                    budgetAmount: 0
                }]);
            }
        }
    }, [phaseData, phaseEntries.length, data?.budgetPhases]);

    // Render functions for customBodyRender
    const renderPhaseCode = (value, tableMeta) => {
        const rowData = phaseEntries[tableMeta.rowIndex];
        if (!rowData || rowData.isTotal) {
            return <strong>{value}</strong>;
        }

        return (
            <Select
                value={rowData.code || ""}
                onChange={(e) => handlePhaseCodeChange(e.target.value, rowData.id)}
                data-testid="phase-code-select"
                placeholder="Select Phase Code"
            >
                <option value="">Select Phase Code</option>
                {phaseData.map((phase) => (
                    <option key={phase.code} value={phase.code}>
                        {phase.code} - {phase.displayName}
                    </option>
                ))}
            </Select>
        );
    };

    const renderPhaseName = (value, tableMeta) => {
        const rowData = phaseEntries[tableMeta.rowIndex];
        if (!rowData || rowData.isTotal) {
            return <strong>{value}</strong>;
        }

        // In edit mode, use the stored displayName, otherwise find from phaseData
        const displayName = rowData.displayName || 
            phaseData?.find((item) => item?.code === rowData?.code)?.displayName || 
            value || '-';

        return displayName;
    }

    const renderWorkDescription = (value, tableMeta) => {
        const rowData = phaseEntries[tableMeta.rowIndex];
        if (!rowData || rowData.isTotal) {
            return <strong>{value}</strong>;
        }
        return (
            <TextField
                onChange={(event) => handleInputChange(event.target.name, event.target.value, rowData.id)}
                value={phaseEntries[tableMeta.rowIndex]?.workDescription}
                textFieldProps={{
                    name: "workDescription",
                }}
                placeholder="Work Description"
                maxLength={150}
                data-testid="work-description"
                id="work-description"
            />
        );
    };

    const renderHours = (value, tableMeta) => {
        const rowData = phaseEntries[tableMeta.rowIndex];
        if (!rowData || rowData.isTotal) {
            return <strong>{value}</strong>;
        }

        return (
            <TextField
                value={rowData.numberOfHours || ""}
                placeholder="# Hours"
                data-testid="budget-phase-hours"
                textFieldProps={{
                    id: "numberOfHours",
                    name: "numberOfHours",
                    min: "0",
                    step: "any"
                }}
                onChange={(e) => handleInputChange(e.target.name, e.target.value, rowData.id)}
                type="number"
            />
        );
    };

    const renderBudgetAmount = (value, tableMeta) => {
        const rowData = phaseEntries[tableMeta.rowIndex];
        if (!rowData || rowData.isTotal) {
            return <strong>{amountFormat(value)}</strong>;
        }

        return (
            <TextField
                value={rowData.budgetAmount || ""}
                placeholder="$ Amount"
                data-testid="budget-phase-amount"
                textFieldProps={{
                    id: "budgetAmount",
                    name: "budgetAmount",
                    min: "0",
                    step: "any"
                }}
                onChange={(e) => handleInputChange(e.target.name, e.target.value, rowData.id)}
                type="number"
            />
        );
    };


    const columns = [
        {
            name: "code",
            label: "Phase Code",
            options: {
                sort: false,
                filter: false,
                setCellHeaderProps: () => ({
                    style: {
                        minWidth: '200px',
                        width: '200px',
                        maxWidth: '200px'
                    }
                }),
                setCellProps: () => ({
                    style: {
                        minWidth: '200px',
                        width: '200px',
                        maxWidth: '200px'
                    }
                }),
                customBodyRender: renderPhaseCode
            }
        },
        {
            name: "displayName",
            label: "Phase Name",
            options: {
                sort: false,
                filter: false,
                customBodyRender: renderPhaseName
            }
        },
        {
            name: "workDescription",
            label: "Work Description",
            options: {
                sort: false,
                filter: false,
                setCellHeaderProps: () => ({
                    style: {
                        minWidth: '350px',
                        width: '350px',
                        maxWidth: '350px'
                    }
                }),
                setCellProps: () => ({
                    style: {
                        width: '350px',
                        maxWidth: '350px'
                    }
                }),
                customBodyRender: renderWorkDescription
            }
        },
        {
            name: "numberOfHours",
            label: "Hours",
            options: {
                sort: false,
                filter: false,
                setCellHeaderProps: () => ({
                    style: {
                        minWidth: '120px',
                        width: '120px',
                        maxWidth: '120px'
                    }
                }),
                setCellProps: () => ({
                    style: {
                        minWidth: '120px',
                        width: '120px',
                        maxWidth: '120px'
                    }
                }),
                customBodyRender: renderHours
            }
        },
        {
            name: "budgetAmount",
            label: "Budget Amount",
            options: {
                sort: false,
                filter: false,
                setCellHeaderProps: () => ({
                    style: {
                        minWidth: '150px',
                        width: '150px',
                        maxWidth: '150px'
                    }
                }),
                setCellProps: () => ({
                    style: {
                        minWidth: '150px',
                        width: '150px',
                        maxWidth: '150px'
                    }
                }),
                customBodyRender: renderBudgetAmount
            }
        },
        {
            name: "action",
            label: "Actions",
            options: {
                filter: false,
                sort: false,
                setCellHeaderProps: () => ({
                    style: {
                        paddingLeft: '32px'
                    }
                }),
                customBodyRender: (value, tableMeta) => {
                    const rowData = phaseEntries[tableMeta.rowIndex];
                    if (!rowData || rowData.isTotal) {
                        return null;
                    }

                    return (
                        <Button
                            id="remove-phase"
                            variant="tertiary"
                            onClick={() => handleRemovePhaseEntry(rowData?.id)}
                            data-testid={`budget-phase-remove`}
                        >
                            {"Remove"}
                        </Button>
                    );
                }
            }
        }

    ];

    const handlePhaseCodeChange = (selectedCode, entryId) => {
        const selectedPhase = phaseData.find(phase => phase.code === selectedCode);

        setPhaseEntries(prevEntries => {
            const updatedEntries = prevEntries.map(entry => {
                if (entry.id === entryId) {
                    return {
                        ...entry,
                        code: selectedCode,
                        displayName: selectedPhase ? selectedPhase.displayName : ""
                    };
                }
                return entry;
            });

            // Call parent callback with updated data
            onPhasesChange(updatedEntries.filter(entry => !entry.isTotal));
            return updatedEntries;
        });
    };

    const handleInputChange = (fieldName, value, id) => {
        setPhaseEntries(prevEntries => {
            const updatedEntries = prevEntries.map(entry => {
                if (entry.id === id) {
                    return {
                        ...entry,
                        [fieldName]: value
                    };
                }
                return entry;
            });

            // Call parent callback with updated data
            onPhasesChange(updatedEntries.filter(entry => !entry.isTotal));
            return updatedEntries;
        });
    };

    const handleAddPhaseEntry = () => {
        const newEntry = {
            id: phaseEntries.length + 1,
            code: "",
            displayName: "",
            workDescription: "",
            numberOfHours: 0,
            budgetAmount: 0
        };
        setPhaseEntries([...phaseEntries, newEntry]);
    };

    const handleRemovePhaseEntry = (id) => {
        const updatedEntries = phaseEntries.filter(entry => entry.id !== id);
        setPhaseEntries(updatedEntries);
    };


    // Calculate totals
    const totalHours = phaseEntries.reduce((sum, entry) => {
        const numberOfHours = parseFloat(entry.numberOfHours) || 0;
        return sum + numberOfHours;
    }, 0);

    const totalAmount = phaseEntries.reduce((sum, entry) => {
        const budgetAmount = parseFloat(entry.budgetAmount) || 0;
        return sum + budgetAmount;
    }, 0);

    // Notify parent component of phase entries changes
    useEffect(() => {
        if (onPhasesChange) {
            onPhasesChange(phaseEntries);
        }
    }, [phaseEntries]);

    const customOptions = {
        selectableRows: "none",
        pagination: false,
        search: false,
        filter: false,
    };

    return (
        <div
            className={classes.budgetTypePhase}
            data-testid="budget-phase"
        >
            {loading || dataLoading ? (
                <Skeleton height="58px" variant="rectangle" />
            ) : (
                <>
                    {phaseData?.length > 0 && <div className={classes.addPhase}>
                        <div
                            className={classes.cursorPointer}
                            data-testid="budget-phase-addEntry"
                            onClick={handleAddPhaseEntry}
                        >
                            <Plus size="medium" />
                            <span className={classes.addNewText} id="add-new-phase">Add Phase</span>
                        </div>
                    </div>}
                    <MuiDataTable
                        data={phaseData.length > 0 ? [...phaseEntries, {
                            id: 'total',
                            code: 'Total',
                            displayName: `${phaseEntries.length} Phase(s)`,
                            workDescription: '',
                            numberOfHours: amountFormat(totalHours),
                            budgetAmount: `$ ${amountFormat(totalAmount)}`,
                            isTotal: true
                        }] : []}
                        columns={columns}
                        customOptions={customOptions}
                        disableSearch={true}
                        disableFilter={true}
                        disablePagination={true}
                        disableExport={true}
                        customClassName={classes.phaseTable}
                        hideAddNewButton={true}
                        noResultsText={NO_PHASES_LINKED_MESSAGE}
                    />
                </>
            )}
        </div>
    );
};

export default AddBudgetByPhase;